import { map, Observable, throwError } from 'rxjs';
import { ChatGptAction, ChatGptIntentChip } from 'src/app/core/types/chatgpt/types-chatgpt';
import { AssistantSearchSimDTO, ChatGptActionPayload, ChatGptGlobalSearchData, ChatGptGlobalSearchResult } from 'src/app/core/interfaces/chatgpt/interfaces-chatgpt';
import { ChatGptService } from './chatgpt-service';
import { CompanyDTO } from 'src/app/core/models/system/company/company.dto';
import { DropdownService } from './dropdown-service';
import { ExceptionsService } from './exceptions-service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';
import { Ticket } from 'src/app/core/models/system/support/ticket-hub/ticket';
import { TicketDTO } from 'src/app/core/models/system/support/ticket-hub/ticket.dto';
import { UserDTO } from 'src/app/core/models/system/user/user.dto';

@Injectable({ providedIn: 'root' })

export class SureSimSearchService {

  public static readonly routeAliasMap: Record<string, string>={
    availablesim    : '/provisioning/devices-available',
    availablesims   : '/provisioning/devices-available',
    companies       : '/companies/companies',
    company         : '/companies/companies',
    dash            : '/',
    dashboard       : '/',
    esimuser        : '/users/users-esim',
    esimusers       : '/users/users-esim',
    liveview        : '/timeline/esim-timeline',
    managesim       : '/provisioning/devices-allocated',
    managesims      : '/provisioning/devices-allocated',
    portal          : '/users/users-portal',
    portaluser      : '/users/users-portal',
    portalusers     : '/users/users-portal',
    provision       : '/provisioning/esim-provision',
    provisionesim   : '/provisioning/esim-provision',
    provisionnewesim: '/provisioning/esim-provision',
    provisionnewsim : '/provisioning/esim-provision',
    provisionsim    : '/provisioning/esim-provision',
    report          : '/reporting/reporting-financial',
    reporting       : '/reporting/reporting-financial',
    reports         : '/reporting/reporting-financial',
    simuser         : '/users/users-esim',
    simusers        : '/users/users-esim',
    supplier        : '/suppliers/suppliers',
    suppliers       : '/suppliers/suppliers',
    ticket          : '/support/ticket-hub',
    tickethub       : '/support/ticket-hub',
    tickets         : '/support/ticket-hub',
    ticketshub      : '/support/ticket-hub',
    timeline        : '/timeline/esim-timeline',
  };

  public static readonly routePageLabelMap: Record<string, string>={
    '/'                              : 'Dashboard',
    '/companies/companies'           : 'Companies',
    '/provisioning/devices-allocated': 'Manage SIMs',
    '/provisioning/devices-available': 'Available SIMs',
    '/provisioning/esim-provision'   : 'Provision SIM',
    '/reporting/reporting-financial' : 'Financial Reports',
    '/suppliers/suppliers'           : 'Suppliers',
    '/support/ticket-hub'            : 'Ticket Hub',
    '/timeline/esim-timeline'        : 'eSIM Timeline',
    '/users/users-esim'              : 'eSIM Users',
    '/users/users-portal'            : 'Portal Users',
  };

  constructor(
    private chatGptService   : ChatGptService,
    private dropdownService  : DropdownService,
    private exceptionsService: ExceptionsService,
    private router           : Router
  ) { }

  //#region Global Search

  public globalSearch(query: string): Observable<ChatGptGlobalSearchResult> {
    try {
      const term=(query ?? '').trim();

      return this.loadGlobalSearchData(term)
             .pipe(
               map(({ closedTickets, companies, openTickets, sims, truncated, users }) => {
                 const companyMatches=this.chatGptService.findMatchingCompanies(companies ?? [], term);
                 const userMatches   =this.chatGptService.findMatchingUsers(users ?? [], term).slice(0, 8);
                 const allTickets    =this.chatGptService.uniqueTicketsById([...(openTickets ?? []), ...(closedTickets ?? [])]);
                 const ticketMatches =this.chatGptService.findMatchingTickets(allTickets, term);

                 return this.buildGlobalSearchResult(companyMatches, userMatches, sims ?? [], ticketMatches, truncated);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  private buildGlobalSearchResult(companyMatches: CompanyDTO[],
                                  userMatches   : UserDTO[],
                                  sims          : AssistantSearchSimDTO[],
                                  ticketMatches : Ticket[],
                                  truncated     : boolean): ChatGptGlobalSearchResult {
    try {
      const simMatches: AssistantSearchSimDTO[]=this.chatGptService.uniqueSimCandidates(sims);
      const actions   : ChatGptAction        []=[];

      companyMatches.forEach(c => actions.push(...this.buildCompanyIntentActions(c.companyName, Number(c.id))));
      ticketMatches.forEach(t => actions.push(...this.buildTicketIntentActions(Number(t.id), this.ticketDtoFromTicket(t), this.ticketMatchLabel(t), t.status, t)));
      userMatches.forEach(u => actions.push(...this.buildUserActionsForMatchedUser(u)));
      simMatches.forEach(s => actions.push(...this.buildSimIntentActionsForSearchSim(s)));

      return {
        actions     : this.uniqueActions(actions),
        companyCount: companyMatches.length,
        simCount    : simMatches.length,
        ticketCount : ticketMatches.length,
        truncated   : truncated,
        userCount   : userMatches.length
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { actions: [], companyCount: 0, simCount: 0, ticketCount: 0, truncated: false, userCount: 0 };
    }
  }
  
  private buildSimIntentActionsForSearchSim(sim: AssistantSearchSimDTO): ChatGptAction[] {
    try {
      const iccid=(sim?.description ?? '').toString();

      if (!iccid)
        return [];

      const actions     =this.buildSimIntentActions(iccid, sim.status ?? undefined);
      const companyId   =Number(sim.companyID ?? 0);
      const userId      =Number(sim.userID ?? 0);
      const userFullname=(sim.userFullname ?? '').trim();

      actions.forEach(action => {
        action.payload={
          ...action.payload,
          companyId  : companyId>0 ? companyId : undefined,
          companyName: sim.companyName || undefined,
          userId     : userId>0 ? userId : undefined,
          userName   : userFullname || undefined
        };
      });

      return actions;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private loadGlobalSearchData(query: string): Observable<ChatGptGlobalSearchData> {
    try {
      return this.chatGptService.search(query)
             .pipe(map(result => ({
               closedTickets: result.ticketsClosed,
               companies    : result.companies,
               openTickets  : result.ticketsOpen,
               sims         : result.sims,
               truncated    : this.chatGptService.isSearchTruncated(result),
               users        : this.uniqueUsersById([...result.portalUsers, ...result.simUsers])
             })));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public resolveSearchCompanyId(curUser: SystemUserDTO, topBarCompanyId: number | undefined): number {
    try {
      const trackedCompanyId=Number(this.dropdownService.trackerCompanyId ?? 0);

      if (Number.isFinite(trackedCompanyId) && trackedCompanyId>0)
        return trackedCompanyId;

      const selectedCompanyId=Number(topBarCompanyId ?? 0);

      if (Number.isFinite(selectedCompanyId) && selectedCompanyId>0)
        return selectedCompanyId;

      const companyId=Number((curUser as any)?.companyId ?? (curUser as any)?.companyID ?? 0);

      return Number.isFinite(companyId) && companyId>0 ? companyId : 0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //#endregion

  //#region Action Builders

  public buildCompanyIntentActions(companyName: string, companyId?: number): ChatGptAction[] {
    try {
      const name     =(companyName ?? '').trim();
      const previewId=Number(companyId ?? -1);

      if (!name)
        return [];

      return [
        {
          type       : 'navigate',
          destination: 'companies',
          payload    : { companyName: name, companyId: previewId>0 ? previewId : undefined },
          label      : previewId>0 ? `Open Company Grid: ${name} (#${previewId})` : `Open Company Grid: ${name}`
        },
        {
          type   : 'open_modal',
          modal  : 'company',
          payload: { companyName: name, companyId: previewId>0 ? previewId : undefined },
          label  : previewId>0 ? `Open Company Form: ${name} (#${previewId})` : `Open Company Form: ${name}`
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public buildSimIntentActions(iccid: string, status?: string, relatedUser?: UserDTO): ChatGptAction[] {
    try {
      const basePayload: ChatGptActionPayload={
        iccid       : iccid,
        entityStatus: status,
        userId      : relatedUser?.id,
        userName    : relatedUser ? (relatedUser.fullname || relatedUser.username || relatedUser.email) : undefined,
        companyId   : relatedUser?.companyID
      };
      return [
        {
          type       : 'navigate',
          destination: 'managesims',
          payload    : basePayload,
          label      : `Open SIMs Grid: ${iccid}`
        },
        {
          type   : 'open_modal',
          modal  : 'manage_sim',
          payload: basePayload,
          label  : `Open SIMs Form: ${iccid}`
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public buildTicketIntentActions(ticketId    : number,
                                  ticket?     : TicketDTO,
                                  labelSuffix?: string,
                                  status?     : string,
                                  knownTicket?: Ticket): ChatGptAction[] {
    try {
      const suffix =(labelSuffix ?? '').trim();
      const details=suffix ? ` (${suffix})` : '';
      return [
        {
          type       : 'navigate',
          destination: 'ticketshub',
          payload    : { ticketId: ticketId, ticketTitle: ticket?.Title, entityStatus: status, ticket: knownTicket },
          label      : `Open Tickets Grid: #${ticketId}${details}`
        },
        {
          type   : 'open_modal',
          modal  : 'ticket',
          payload: {
            ticketId    : ticketId,
            companyId   : ticket?.CompanyId,
            userId      : ticket?.AffectedUserId || ticket?.UserId,
            iccid       : ticket?.ICCID,
            ticketTitle : ticket?.Title,
            entityStatus: status,
            ticket      : knownTicket
          },
          label: `Open Tickets Form: #${ticketId}${details}`
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public buildUserActionsForMatchedUser(user: UserDTO): ChatGptAction[] {
    try {
      const display      =user.fullname || user.username || user.email;
      const simActions   =user.eSimUser   ? this.buildUserIntentActions(display, 'sim',    user) : [];
      const portalActions=user.portalUser ? this.buildUserIntentActions(display, 'portal', user) : [];

      if (simActions.length===0 && portalActions.length===0)
        return this.buildUserIntentActions(display, 'portal', user);

      return [
        ...(portalActions[0] ? [portalActions[0]] : []),
        ...(portalActions[1] ? [portalActions[1]] : []),
        ...(simActions   [0] ? [simActions   [0]] : []),
        ...(simActions   [1] ? [simActions   [1]] : []),
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public buildUserIntentActions(userName: string, userType: 'portal' | 'sim', matchedUser?: UserDTO): ChatGptAction[] {
    try {
      const name=(userName ?? '').trim();

      if (!name)
        return [];

      const navigateDestination=userType==='sim' ? 'simusers' : 'portalusers';
      const modal              =userType==='sim' ? 'user_sim' : 'user_portal';
      const userTypeLabel      =userType==='sim' ? 'SIM User' : 'Portal User';

      const payload: ChatGptActionPayload={
        userName   : name,
        userId     : matchedUser?.id,
        companyId  : matchedUser?.companyID,
        userEmail  : matchedUser?.email,
        companyName: (matchedUser as any)?.companyName
      };

      return [
        {
          type       : 'navigate',
          destination: navigateDestination,
          payload    : payload,
          label      : matchedUser?.id ? `Open ${userTypeLabel} Grid: ${name} (#${matchedUser.id})` : `Open ${userTypeLabel} Grid: ${name}`
        },
        {
          type   : 'open_modal',
          modal  : modal,
          payload: payload,
          label  : matchedUser?.id ? `Open ${userTypeLabel} Form: ${name} (#${matchedUser.id})` : `Open ${userTypeLabel} Form: ${name}`
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public ticketDtoFromTicket(ticket: Ticket): TicketDTO {
    try {
      return {
        Id            : Number(ticket?.id ?? 0),
        CompanyId     : Number(ticket?.company_id ?? 0),
        UserId        : Number(ticket?.userId ?? 0),
        AffectedUserId: Number(ticket?.affectedUserId ?? 0),
        ICCID         : ticket?.iccid ?? '',
        Title         : ticket?.title ?? '',
        Type          : ticket?.type ?? '',
        Details       : ticket?.details ?? '',
        PriorityId    : Number(ticket?.priorityId ?? 0),
        EnteredBy     : Number(ticket?.enteredBy ?? 0),
        HighPriority  : Boolean(ticket?.highPriority),
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return <TicketDTO>{};
    }
  }

  public ticketMatchLabel(ticket: Ticket): string {
    try {
      const title    =(ticket?.title ?? '').trim();
      const requester=(ticket?.requester ?? '').trim();

      if (title && requester)
        return `${title} - ${requester}`;

      if (title)
        return title;

      if (requester)
        return requester;

      return '';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public uniqueActions(actions: ChatGptAction[]): ChatGptAction[] {
    try {
      const seen                   =new Set<string>();
      const result: ChatGptAction[]=[];

      actions.forEach(action => {
        const key=JSON.stringify(action);

        if (!seen.has(key)) {
          seen.add(key);
          result.push(action);
        }
      });

      return result;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public uniqueUsersById(users: UserDTO[]): UserDTO[] {
    try {
      const seen             =new Set<number>();
      const result: UserDTO[]=[];

      (users ?? []).forEach(user => {
        const id=Number(user?.id ?? -1);

        if (!Number.isFinite(id) || id <= 0 || seen.has(id))
          return;

        seen.add(id);
        result.push(user);
      });

      return result;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  //#endregion

  //#region Action Classification

  public actionCardKey(action: ChatGptAction): string {
    try {
      const payload=action.payload;

      if (payload?.ticketId)
        return `ticket:${payload.ticketId}`;

      if (payload?.iccid)
        return `sim:${payload.iccid.toLowerCase()}`;

      if (payload?.userId)
        return `user-id:${payload.userId}`;

      if (payload?.userName)
        return `user-name:${payload.userName.toLowerCase()}`;

      if (payload?.companyId)
        return `company-id:${payload.companyId}`;

      if (payload?.companyName)
        return `company-name:${payload.companyName.toLowerCase()}`;

      if (action.type==='open_modal')
        return `modal:${action.modal}:${this.actionLabel(action)}`;

      return `navigate:${action.destination}:${this.actionLabel(action)}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public actionCardKind(action: ChatGptAction): ChatGptIntentChip {
    try {
      const payload=action.payload;

      if (payload?.ticketId || (action.type==='open_modal' && action.modal==='ticket') || (action.type==='navigate' && action.destination.includes('ticket')))
        return 'ticket';

      if (payload?.iccid ||
          (action.type==='open_modal' && (action.modal==='timeline' || action.modal==='manage_sim')) ||
          (action.type==='navigate' && (action.destination==='managesims' || action.destination==='timeline')))
        return 'sim';

      if (payload?.userId || payload?.userName || (action.type==='open_modal' && (action.modal==='user_portal' || action.modal==='user_sim')))
        return 'user';

      return 'company';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'company';
    }
  }

  public actionCardPreviewItems(action: ChatGptAction): string[] {
    try {
      const payload        =action.payload;
      const items: string[]=[];

      if (payload?.companyName)
        items.push(`Company: ${payload.companyName}`);

      if (payload?.companyId)
        items.push(`Company ID: ${payload.companyId}`);

      if (payload?.userName)
        items.push(`User: ${payload.userName}`);

      if (payload?.userId)
        items.push(`User ID: ${payload.userId}`);

      if (payload?.userEmail)
        items.push(`Email: ${payload.userEmail}`);

      if (payload?.iccid)
        items.push(`ICCID: ${payload.iccid}`);

      if (payload?.ticketId)
        items.push(`Ticket: #${payload.ticketId}`);

      if (payload?.ticketTitle)
        items.push(`Title: ${payload.ticketTitle}`);

      if (payload?.entityStatus)
        items.push(`Status: ${payload.entityStatus}`);

      return items.slice(0, 4);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public actionLabel(action: ChatGptAction): string {
    try {
      if (action.label?.trim())
        return action.label.trim();

      if (action.type==='navigate')
        return `Open ${action.destination}`;

      return `Open ${action.modal.replace('_', ' ')}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'Run Action';
    }
  }

  public actionDisplayOrder(action: ChatGptAction): number {
    try {
      if (action.type==='navigate') {
        const destination=(action.destination ?? '').toLowerCase();

        if (destination==='companies' || destination==='company')
          return 5;

        if (destination==='portalusers' || destination==='portaluser')
          return 10;

        if (destination==='simusers' || destination==='simuser' || destination==='esimusers' || destination==='esimuser')
          return 30;

        if (destination==='managesims' || destination==='managesim')
          return 25;

        if (destination==='ticketshub' || destination==='ticket' || destination==='tickets')
          return 27;

        return 90;
      }

      const modal=(action.modal ?? '').toLowerCase();

      if (modal==='company')
        return 6;

      if (modal==='user_portal')
        return 20;

      if (modal==='user_sim')
        return 40;

      if (modal==='manage_sim')
        return 26;

      if (modal==='ticket')
        return 28;

      return 95;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //#endregion

  //#region Navigation

  public navigate(destination: string, payload?: ChatGptActionPayload): boolean {
    try {
      const destinationNormalized=(destination ?? '').trim().toLowerCase();
      const routePath            =SureSimSearchService.routeAliasMap[destinationNormalized];
      const payloadCompanyId     =Number(payload?.companyId ?? 0);

      if (!routePath)
        return false;

      if (routePath==='/companies/companies' && payload?.companyName) {
        this.router.navigate([routePath], { queryParams: { company: payload.companyName } });

      } else if (routePath==='/users/users-portal' && payload?.userName) {
        if (payloadCompanyId>0 && this.dropdownService.trackingCompanyIdIsOn())
          this.dropdownService.trackerCompanyId=payloadCompanyId;

        this.router.navigate([routePath], { queryParams: { user: payload.userName, companyId: payloadCompanyId>0 ? payloadCompanyId : undefined } });

      } else if (routePath==='/users/users-esim' && payload?.userName) {
        if (payloadCompanyId>0 && this.dropdownService.trackingCompanyIdIsOn())
          this.dropdownService.trackerCompanyId=payloadCompanyId;

        this.router.navigate([routePath], { queryParams: { user: payload.userName, companyId: payloadCompanyId>0 ? payloadCompanyId : undefined } });

      } else if (routePath==='/provisioning/devices-allocated' && payload?.iccid) {
        if (payloadCompanyId>0 && this.dropdownService.trackingCompanyIdIsOn())
          this.dropdownService.trackerCompanyId=payloadCompanyId;

        this.router.navigate([routePath], { queryParams: { sim: payload.iccid, companyId: payloadCompanyId>0 ? payloadCompanyId : undefined } });

      } else if (routePath==='/support/ticket-hub' && payload?.ticketId) {
        this.router.navigate([routePath], { queryParams: { ticket: payload.ticketId } });

      } else {
        this.router.navigate([routePath]);
      }

      return true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  //#endregion
}
